import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import notify from 'devextreme/ui/notify';
import FileManager, {
  Toolbar, Item, ItemView, Details, Column,
} from 'devextreme-react/file-manager';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';

import { useCommitteeDocuments } from '~/services';
import { calcGridHeight } from '~/helpers/ui.jsx';
import { appConst } from '~/app-const';

// Documents are stored without a content type, so the download response is
// always application/octet-stream. Infer a renderable MIME type from the file
// extension so the browser can display the file inline instead of downloading.
const mimeTypeFromFileName = (fileName) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return appConst.mimeTypesByExtension[extension];
};

// Context menu items. "rename" is only offered for files (folder rename is not
// supported), toggled via `visible`.
const contextMenuItems = (renameVisible) => [
  { text: 'View', icon: 'eyeopen' },
  'create',
  { name: 'rename', visible: renameVisible },
  'delete',
  'refresh',
];

// Maps a raw ListAttachments item to a DevExtreme file-system item. The original
// fields (Name, ...) are spread so fileSystemItem.dataItem keeps the full blob
// path used for download.
const toFileSystemItem = (item) => (item.IsDirectory
  ? { name: item.FileName, isDirectory: true }
  : {
    ...item,
    name: item.FileName,
    isDirectory: false,
    size: item.Size,
    // DevExtreme's "Date Modified" column expects a Date, not an ISO string.
    dateModified: item.DateUploaded ? new Date(item.DateUploaded) : undefined,
  });

// FileManager for a committee's documents. All HTTP lives in the
// useCommitteeDocuments hook; this component is the DevExtreme/UI glue.
// The parent can call setHeight() (via ref) when the Documents tab becomes visible.
export const CommitteeFileManager = forwardRef(function CommitteeFileManager({ committeeId }, ref) {
  const documents = useCommitteeDocuments(committeeId);
  const fileManagerRef = useRef();

  // Sizes the FileManager to fit the popup, only once it is actually visible;
  // measuring it while its tab is hidden yields top = 0 and an over-tall height.
  const setHeight = useCallback(() => {
    requestAnimationFrame(() => {
      const instance = fileManagerRef.current?.instance();
      const element = instance?.element();
      if (element && element.offsetParent !== null) {
        instance.option('height', calcGridHeight(element));
      }
    });
  }, []);

  useImperativeHandle(ref, () => ({ setHeight }), [setHeight]);

  useEffect(() => {
    window.addEventListener('resize', setHeight);
    return () => window.removeEventListener('resize', setHeight);
  }, [setHeight]);

  // Fetches the document and opens it in a new tab via a blob: URL
  // (PDFs/images render inline; other types download).
  const viewDocument = useCallback(async (fileName) => {
    try {
      const rawBlob = await documents.download(fileName);
      const contentType = mimeTypeFromFileName(fileName) || rawBlob.type || 'application/octet-stream';
      const objectUrl = window.URL.createObjectURL(new Blob([rawBlob], { type: contentType }));
      window.open(objectUrl, '_blank', 'noopener noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }, [documents]);

  const uploadFileChunk = useCallback(async (fileData, uploadInfo, destinationDirectory) => {
    // DevExtreme splits large files into chunks and calls this once per chunk,
    // but the backend accepts the whole file (available in fileData). Upload only
    // on the final chunk so we make a single request.
    if (uploadInfo.chunkIndex < uploadInfo.chunkCount - 1) {
      return;
    }

    // Always upload with replace = true: a same-named file is overwritten (no
    // duplicate-file prompt), which keeps multi-file uploads working reliably.
    const path = destinationDirectory?.path ?? '';
    await documents.upload(fileData, fileData.name, path, true);
  }, [documents]);

  const fileSystemProvider = useMemo(() => new CustomFileSystemProvider({
    getItems: async (pathInfo) => (await documents.listAttachments(pathInfo?.path ?? '')).map(toFileSystemItem),
    createDirectory: (parentDirectory, name) => documents.createFolder(name, parentDirectory?.path ?? ''),
    uploadFileChunk,
    deleteItem: (item) => documents.deleteItem(item.path, item.isDirectory),
    renameItem: (item, newName) => documents.rename(item.path, newName, item.isDirectory),
  }), [documents, uploadFileChunk]);

  // The FileManager rebuilds its context menu from the option each time it opens,
  // so toggle the rename item's visibility based on the right-clicked item here.
  const onContextMenuShowing = useCallback((e) => {
    e.component.option('contextMenu.items', contextMenuItems(!e.fileSystemItem?.isDirectory));
  }, []);

  // Row-level "View" from the file context menu (right-click a document).
  const onContextMenuItemClick = useCallback(async (e) => {
    if (e.itemData.text === 'View' && e.fileSystemItem && !e.fileSystemItem.isDirectory) {
      await viewDocument(e.fileSystemItem.dataItem.Name);
    }
  }, [viewDocument]);

  // Double-clicking (opening) a document also views it.
  const onSelectedFileOpened = useCallback(async (e) => {
    if (e.file && !e.file.isDirectory) {
      await viewDocument(e.file.dataItem.Name);
    }
  }, [viewDocument]);

  return (
    <FileManager
      id="committeeDocumentsFileManager"
      ref={fileManagerRef}
      fileSystemProvider={fileSystemProvider}
      permissions={{ upload: true, create: true, delete: true, rename: true }}
      contextMenu={{ items: contextMenuItems(true) }}
      onContextMenuShowing={onContextMenuShowing}
      onContextMenuItemClick={onContextMenuItemClick}
      onSelectedFileOpened={onSelectedFileOpened}
      onContentReady={setHeight}
    >
      {/* Declare the detail columns explicitly so they are always present and
          never dropped when the listing is re-rendered after an operation. */}
      <ItemView>
        <Details>
          <Column dataField="thumbnail" />
          <Column dataField="name" />
          <Column dataField="dateModified" />
          <Column dataField="size" />
          <Column dataField="UploadedBy" caption="Uploaded By" />
        </Details>
      </ItemView>
      <Toolbar>
        <Item name="create" />
        <Item name="upload" />
        <Item name="refresh" />
      </Toolbar>
    </FileManager>
  );
});

CommitteeFileManager.propTypes = {
  committeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
